import React, { useEffect, useState } from "react";
import { Box, Button, Stack, TextField, Typography, Paper, IconButton } from "@mui/material";
import { useAppStateContext } from "../../state/AppContextProvider";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import SaveIcon from "@mui/icons-material/Save";
import CancelIcon from "@mui/icons-material/Cancel";

export interface GlossaryItem {
    id?: string;
    term: string;
    definition: string;
    tags?: string;
}

const GlossaryManager: React.FC = () => {
    const { glossary, addGlossary, updateGlossary, deleteGlossary } = useAppStateContext() as any;
    const [editingId, setEditingId] = useState<string | null>(null);
    const [editTerm, setEditTerm] = useState("");
    const [editDefinition, setEditDefinition] = useState("");
    const [editTags, setEditTags] = useState("");
    const [newTerm, setNewTerm] = useState("");
    const [newDefinition, setNewDefinition] = useState("");
    const [newTags, setNewTags] = useState("");
    const [termError, setTermError] = useState(false);
    const [definitionError, setDefinitionError] = useState(false);
    const isEditing = editingId !== null;

    const handleFormSubmit = async () => {
        const hasTermError = !newTerm.trim();
        const hasDefinitionError = !newDefinition.trim();
        
        setTermError(hasTermError);
        setDefinitionError(hasDefinitionError);
        
        if (hasTermError || hasDefinitionError) {
            return;
        }
        if (isEditing) {
            await updateGlossary({ id: editingId, term: newTerm, definition: newDefinition, tags: newTags }, new AbortController().signal);
        } else {
            await addGlossary({ term: newTerm, definition: newDefinition, tags: newTags }, new AbortController().signal);
        }
        setEditingId(null);
        setNewTerm("");
        setNewDefinition("");
        setNewTags("");
        setTermError(false);
        setDefinitionError(false);
    };

    const handleEditGlossary = (item: GlossaryItem) => {
        setEditingId(item.id || "");
        setNewTerm(item.term);
        setNewDefinition(item.definition);
        setNewTags(item.tags || "");
    };

    const handleCancelEdit = () => {
        setEditingId(null);
        setNewTerm("");
        setNewDefinition("");
        setNewTags("");
        setTermError(false);
        setDefinitionError(false);
    };

    const handleDeleteGlossary = async (item: GlossaryItem) => {
        if (!item.id) return;
        await deleteGlossary(item, new AbortController().signal);
    };

    return (
        <Box sx={{ p: 3 }}>
            <Typography variant="h6" sx={{ mb: 2 }}>Manage Glossary</Typography>
            <Stack spacing={3}>
                {/* Add New Glossary Item */}
                <Paper sx={{ p: 2 }} >
                    <Typography variant="subtitle1" sx={{ mb: 2 }}>{isEditing ? "Edit Term" : "Add New Term"}</Typography>
                    <TextField
                        label="Term"
                        value={newTerm}
                        onChange={e => {
                            setNewTerm(e.target.value);
                            if (e.target.value.trim()) setTermError(false);
                        }}
                        fullWidth
                        required
                        error={termError}
                        helperText={termError ? "Term is required" : ""}
                        sx={{ mb: 2 }}
                    />
                    <TextField
                        label="Definition"
                        value={newDefinition}
                        onChange={e => {
                            setNewDefinition(e.target.value);
                            if (e.target.value.trim()) setDefinitionError(false);
                        }}
                        fullWidth
                        multiline
                        minRows={1}
                        required
                        error={definitionError}
                        helperText={definitionError ? "Definition is required" : ""}
                        sx={{ mb: 2 }}
                    />
                    <TextField
                        label="Tags (semicolon separated)"
                        placeholder="e.g. pov; point of view; (pov)"
                        value={newTags}
                        onChange={e => setNewTags(e.target.value)}
                        fullWidth
                        sx={{ mb: 2 }}
                    />
                    <Stack direction="row" spacing={1}>
                        <Button variant="contained" onClick={handleFormSubmit}>
                            {isEditing ? "Save" : "Add Term"}
                        </Button>
                        {isEditing && (
                            <Button variant="outlined" color="secondary" onClick={handleCancelEdit}>
                                Cancel
                            </Button>
                        )}
                    </Stack>
                </Paper>
                {/* Glossary List */}
                <Box sx={{ maxHeight: "22vh", overflowY: "auto" }}>
                    {glossary?.map((item: GlossaryItem) => (
                        <Paper key={item.id || item.term} sx={{ p: 2, display: "flex", alignItems: "center", mb: 1 }}>
                            <Box sx={{ flex: 1 }}>
                                <Typography variant="subtitle1">{item.term}</Typography>
                                <Typography variant="body2">{item.definition}</Typography>
                                {item.tags && (
                                    <Typography variant="caption" sx={{ display: "block", mt: 0.5, color: "text.secondary" }}>
                                        Tags: {item.tags}
                                    </Typography>
                                )}
                            </Box>
                            <Stack direction="row" spacing={1}>
                                <IconButton
                                    onClick={() => handleEditGlossary(item)}
                                    sx={{ color: "#1976d2" }} // Blue pencil
                                >
                                    <EditIcon />
                                </IconButton>
                                <IconButton onClick={() => handleDeleteGlossary(item)} color="error"><DeleteIcon /></IconButton>
                            </Stack>
                        </Paper>
                    ))}
                </Box>
            </Stack>
        </Box>
    );
};
export default GlossaryManager;